import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	Clock,
	Loader2,
	Mail,
	MessageSquare,
	Search,
	User,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { BrandCard, BrandCardContent } from "@/components/brand/brand-card";
import { BrandInput } from "@/components/brand/brand-input";
import { BrandSelect } from "@/components/brand/brand-select";
import { Header } from "@/components/Header";
import { useSession } from "@/hooks/auth-hooks";

export const Route = createFileRoute("/admin/feedback")({
	component: AdminFeedbackPage,
});

type FeedbackItem = {
	id: string;
	category?: string;
	contactEmail?: string;
	createdAt: number;
	message: string;
	userEmail?: string;
	userName?: string;
	user?: Array<{ id: string; email?: string; name?: string }>;
};

const CATEGORY_STYLES: Record<string, string> = {
	bug: "bg-red-500/15 text-red-400 ring-red-500/20",
	idea: "bg-blue-500/15 text-blue-400 ring-blue-500/20",
	ux: "bg-purple-500/15 text-purple-400 ring-purple-500/20",
	integrations: "bg-pink-500/15 text-pink-400 ring-pink-500/20",
	onboarding: "bg-violet-500/15 text-violet-400 ring-violet-500/20",
	other: "bg-stone-500/15 text-stone-400 ring-stone-500/20",
};

const CATEGORY_OPTIONS = [
	{ value: "all", label: "All categories" },
	{ value: "bug", label: "Bug" },
	{ value: "idea", label: "Idea" },
	{ value: "ux", label: "UX" },
	{ value: "integrations", label: "Integrations" },
	{ value: "onboarding", label: "Onboarding" },
	{ value: "other", label: "Other" },
];

function AdminFeedbackPage() {
	const { session, user, isPending } = useSession();
	const navigate = useNavigate();
	const [search, setSearch] = useState("");
	const [categoryFilter, setCategoryFilter] = useState<string>("all");

	const isAdmin = user?.role === "admin";

	useEffect(() => {
		if (isPending) return;
		if (!session || !user) {
			navigate({ to: "/login", replace: true });
			return;
		}
		if (!isAdmin) {
			navigate({ to: "/dashboard", replace: true });
		}
	}, [isPending, session, user, isAdmin, navigate]);

	const { data: feedbackItems = [], isLoading } = useQuery({
		queryKey: ["admin-feedback"],
		queryFn: async () => {
			const res = await fetch("/api/admin/list-feedback", {
				credentials: "include",
			});
			if (!res.ok) throw new Error("Failed to load feedback");
			const data = await res.json();
			return (data.feedback ?? []) as FeedbackItem[];
		},
		enabled: !isPending && !!session && !!user && isAdmin,
	});

	const sortedFeedback = useMemo(() => {
		return feedbackItems.toSorted((a, b) => b.createdAt - a.createdAt);
	}, [feedbackItems]);

	const filteredFeedback = useMemo(() => {
		let items = sortedFeedback;

		if (categoryFilter !== "all") {
			items = items.filter((f) => (f.category ?? "other") === categoryFilter);
		}

		const query = search.trim().toLowerCase();
		if (!query) return items;

		return items.filter((f) => {
			const message = f.message.toLowerCase();
			const email = (f.userEmail ?? f.contactEmail ?? "").toLowerCase();
			const name = (f.userName ?? "").toLowerCase();
			return (
				message.includes(query) || email.includes(query) || name.includes(query)
			);
		});
	}, [sortedFeedback, search, categoryFilter]);

	if (isPending || isLoading || !isAdmin) {
		return (
			<div className="min-h-svh flex items-center justify-center bg-background">
				<Loader2 className="size-8 animate-spin text-primary" />
			</div>
		);
	}

	return (
		<div className="min-h-svh bg-background">
			<Header />

			<main className="max-w-5xl mx-auto px-4 py-8">
				<div className="mb-8">
					<h1 className="text-2xl font-normal text-foreground tracking-[-0.04em]">
						Feedback
					</h1>
					<p className="mt-1 text-base text-muted-foreground">
						{feedbackItems.length} submission
						{feedbackItems.length !== 1 ? "s" : ""} from connected agents
					</p>
				</div>

				<div className="flex flex-col sm:flex-row gap-3 mb-6">
					<BrandInput
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Search feedback..."
						icon={<Search className="size-4" />}
						className="sm:max-w-xs"
					/>
					<BrandSelect
						value={categoryFilter}
						onChange={(e) => setCategoryFilter(e.target.value)}
						options={CATEGORY_OPTIONS}
						className="sm:w-48"
					/>
				</div>

				<div className="space-y-3">
					{filteredFeedback.map((f) => {
						const category = f.category ?? "other";
						const categoryStyle =
							CATEGORY_STYLES[category] ?? CATEGORY_STYLES.other;
						const displayEmail = f.userEmail ?? f.contactEmail ?? null;
						const displayName = f.userName ?? null;

						return (
							<BrandCard key={f.id}>
								<BrandCardContent className="p-5">
									<div className="flex items-start justify-between gap-4">
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-2.5 mb-3">
												<span
													className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-normal ring-1 ring-inset ${categoryStyle}`}
												>
													{category}
												</span>
												<span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
													<Clock className="size-3" />
													{new Date(f.createdAt).toLocaleDateString(undefined, {
														month: "short",
														day: "numeric",
														year: "numeric",
													})}{" "}
													{new Date(f.createdAt).toLocaleTimeString(undefined, {
														hour: "2-digit",
														minute: "2-digit",
													})}
												</span>
											</div>

											<p className="text-base text-foreground leading-relaxed whitespace-pre-wrap">
												{f.message}
											</p>

											{(displayName || displayEmail) && (
												<div className="flex items-center gap-3 mt-3 pt-3 border-t border-border/50">
													{displayName && (
														<span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
															<User className="size-3" />
															{displayName}
														</span>
													)}
													{displayEmail && (
														<span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
															<Mail className="size-3" />
															{displayEmail}
														</span>
													)}
												</div>
											)}
										</div>
									</div>
								</BrandCardContent>
							</BrandCard>
						);
					})}

					{filteredFeedback.length === 0 && (
						<BrandCard>
							<BrandCardContent className="py-16">
								<div className="text-center">
									<MessageSquare className="size-10 text-muted-foreground/40 mx-auto mb-3" />
									<p className="text-base text-muted-foreground">
										{search || categoryFilter !== "all"
											? "No feedback matches your filters"
											: "No feedback received yet"}
									</p>
								</div>
							</BrandCardContent>
						</BrandCard>
					)}
				</div>
			</main>
		</div>
	);
}
